// Small presentational atoms shared across the marketing sections (mockup
// market/heroes.jsx + landing-ai.jsx helpers: AIChip, eyebrow label, the
// two-part display heading with a serif-italic emerald accent). Visual-only;
// callers pass already-translated strings so the no-literal lint stays
// authoritative at the call site.

import { Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";

/** Emerald pill with a sparkle — the "AI" chip above marketing headlines. */
export function AIChip({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[var(--mkt-tint-accent)] px-3.5 py-1.5 font-sans text-[13px] font-semibold text-[var(--accent-hover)]">
      <Sparkles size={14} aria-hidden="true" /> {children}
    </span>
  );
}

/** Uppercase tracked eyebrow label above a section heading. */
export function Eyebrow({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}): React.ReactElement {
  return (
    <p
      className={cn(
        "font-sans text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--accent)]",
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * Two-part section heading: an Outfit-display lead + a serif-italic emerald
 * accent (the novem marketing headline shape). `lead` and `accent` are
 * pre-translated strings.
 */
export function DisplayHeading({
  lead,
  accent,
  as: Tag = "h2",
  className,
}: {
  readonly lead: string;
  readonly accent: string;
  readonly as?: "h1" | "h2" | "h3";
  readonly className?: string;
}): React.ReactElement {
  return (
    <Tag
      className={cn(
        "m-0 font-display font-semibold leading-[1.08] tracking-[-0.025em] text-[var(--fg-primary)]",
        className,
      )}
    >
      {lead}{" "}
      <span className="font-serif font-normal italic text-[var(--accent)]">{accent}</span>
    </Tag>
  );
}
